import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

export interface Voice {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  voiceType: string;
  category: string;
  intentType: string;
  topicScope: string;
  city: string | null;
  state: string | null;
  createdAt: string;
  /** NULL on an anonymous post — the server strips the author object rather
   *  than sending a placeholder name, so every renderer must handle absence. */
  user: { id: string; name: string; avatarUrl: string | null } | null;
  standCount?: number;
  storyCount?: number;
  commentCount?: number;
}

export function listVoices(params?: {
  q?: string;
  page?: number;
  limit?: number;
  category?: string;
  voiceType?: string;
  /** One member's voices. Your own id gives your Awaaz list, anonymous ones included. */
  userId?: string;
}) {
  return api.get<{
    success: true;
    data: { voices: Voice[]; meta: PaginatedResponse<unknown>["meta"] };
  }>("/voices", params as Record<string, string | number | boolean | undefined>);
}

// Backend enums (voices.schema.ts).
export const VOICE_TYPES = [
  "issue",
  "discussion",
  "celebration",
  "announcement",
  "local",
  "question",
] as const;

export const VOICE_CATEGORIES = [
  "visa_immigration",
  "policy_government",
  "discrimination",
  "community_safety",
  "healthcare",
  "education",
  "business",
  "cultural",
  "festivals",
  "achievements",
  "local_events",
  "food",
  "entertainment",
  "sports",
  "technology",
  "career",
  "family",
  "other",
] as const;

/** The origin screen's vocabulary — strict on the way in. */
export const DECLARED_ORIGINS = [
  "original_capture",
  "screenshot",
  "social_repost",
  "forwarded",
  "news_clip",
  "ai_generated",
] as const;

export interface VoiceMediaInput {
  /** One of YOUR uploads — the server copies url/type/capture/credentials
   *  from the upload row; none of them can be asserted here. */
  uploadId: string;
  altText?: string;
  /** Omit = the origin screen was dismissed; dismissing publishes. Ignored
   *  for camera-attested uploads. social_repost/news_clip need sourceUrl. */
  declaredOrigin?: (typeof DECLARED_ORIGINS)[number];
  sourceUrl?: string;
}

/** One carousel item on the wire. Vocabulary fields are OPEN strings. */
export interface VoiceMediaItem {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  mediaType: string;
  position: number;
  altText: string | null;
  captureSource: string;
  declaredOrigin: string | null;
  sourceUrl: string | null;
  c2paPresent: boolean;
  c2paVerified: boolean;
  /** full | marked | reduced. BINDING: reduced renders smaller and quieter —
   *  in place, fully readable, never hidden or filtered. */
  presentationWeight: string;
  provenanceLabel: string;
}

export interface CreateVoiceInput {
  title: string; // 5–200
  description: string; // 10–10000
  voiceType: (typeof VOICE_TYPES)[number];
  category: (typeof VOICE_CATEGORIES)[number];
  media?: VoiceMediaInput[];
}

export function createVoice(input: CreateVoiceInput) {
  // Wrapped in `voice`, like every other single-voice response here.
  return api.post<ApiResponse<{ voice: Voice }>>("/voices", input);
}

export interface VoiceDetail extends Voice {
  status: string;
  standCount: number;
  storyCount: number;
  commentCount: number;
  hasStood: boolean;
  userStance?: string;
  userChip?: string | null;
  stanceCounts: Record<string, number>;
  chipCounts: Record<string, number>;
  /** The carousel, ordered. imageUrl/videoUrl are one-release aliases of
   *  the first image / first video here. */
  media?: VoiceMediaItem[];
}

/** A closeness label — "From Maharashtra". Never a score, never a count. */
export interface StandingLabel {
  label: string;
  tier: number;
}

export interface VoiceComment {
  id: string;
  content: string;
  /** Null when the commenter posted anonymously — see VoiceSummary.user. */
  user: { id: string; name: string; avatarUrl: string | null } | null;
  upvoteCount: number;
  createdAt: string;
  replyCount: number;
  /** Where this reply stands. Only ever set on a contested post; an OPEN
   *  string on the wire so a sixth position cannot break this client. */
  position?: string | null;
  /** Required on correct_record — a correction without a source is refused. */
  sourceUrl?: string | null;
  /** Who is close to the subject. Empty on a civic post, and that silence
   *  is the correct answer rather than a gap. */
  standing?: StandingLabel[];
}

/**
 * The two-mode lead on a contested thread. Null on ordinary posts AND when
 * nothing qualifies — a labelled empty container is worse than none.
 * `mode` is an open string: a future mode must not break this decoder.
 */
export interface VoiceTopSlot {
  mode: string;
  label: string;
  items: VoiceComment[];
}

/**
 * The detail response nests under `voice` — `{ data: { voice: {...} } }`.
 *
 * This was typed as the bare voice, so every caller read `data.data.title` as
 * undefined and the detail page white-screened on the first `user.name`. The
 * page had been broken for every voice, which is worth remembering about
 * coverage: the path was reachable AND wired to a page, and still dead. Only
 * opening it finds that.
 */
export function getVoice(idOrSlug: string) {
  return api.get<ApiResponse<{ voice: VoiceDetail }>>(`/voices/${idOrSlug}`);
}

// stance defaults to 'support' server-side; chip is optional.
export function standWithVoice(id: string, stance = "support") {
  return api.post<ApiResponse<{ stand: unknown }>>(`/voices/${id}/stand`, {
    stance,
  });
}

export function removeStand(id: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/voices/${id}/stand`);
}

export function getVoiceComments(id: string, params?: { page?: number; limit?: number }) {
  return api.get<
    ApiResponse<{
      comments: VoiceComment[];
      topSlot: VoiceTopSlot | null;
      meta: PaginatedResponse<unknown>["meta"];
    }>
  >(`/voices/${id}/comments`, params as Record<string, string | number | boolean | undefined>);
}

export function createVoiceComment(id: string, content: string) {
  return api.post<ApiResponse<{ comment: VoiceComment }>>(
    `/voices/${id}/comments`,
    { content },
  );
}

export function upvoteVoiceComment(commentId: string) {
  return api.post<ApiResponse<{ success: boolean }>>(
    `/voices/comments/${commentId}/upvote`,
    {},
  );
}

export function deleteVoiceComment(commentId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/voices/comments/${commentId}`);
}

// ============================================================================
// A voice is not one thread. It has four lanes hanging off it, and they are
// four because they answer different questions:
//
//   stands   — who agrees, and how (a stance, optionally a chip)
//   stories  — "this happened to me too", the lived evidence
//   updates  — what has since become KNOWN: official word, expert, news
//   actions  — what a reader can DO about it, and how many already did
//
// Shapes below are read from docs/api/openapi.json, never from memory. When
// they disagree with this file, the spec is right.
// ============================================================================

export const STORY_REACTIONS = ["heart", "solidarity", "strength"] as const;
export type StoryReaction = (typeof STORY_REACTIONS)[number];

export const UPDATE_TYPES = ["official", "expert", "community", "news", "action"] as const;

/**
 * The three entry types that CLAIM a source, and so must carry one.
 *
 * This is the whole guard on the ledger being open: anyone signed in may add
 * an entry, and what keeps it a record rather than a comment section is that
 * the load-bearing types arrive with evidence.
 */
export const SOURCED_UPDATE_TYPES = ["official", "expert", "news"] as const;

export const ACTION_TYPES = [
  "petition",
  "write_representative",
  "share_social",
  "find_provider",
  "attend_event",
  "donate",
  "volunteer",
] as const;

// Why the poster spoke — it changes what a good response looks like, which is
// why it is editable after the fact.
export const VOICE_INTENTS = [
  "share_experience",
  "seeking_help",
  "raising_awareness",
  "celebrating",
  "seeking_opinion",
  "local_alert",
] as const;

export interface VoiceMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VoiceStand {
  id: string;
  stance: string;
  chip: string | null;
  user: { id: string; name: string; avatarUrl: string | null } | null;
  createdAt: string;
}

export interface VoiceStory {
  id: string;
  content: string;
  user: { id: string; name: string; avatarUrl: string | null } | null;
  reactionCounts: Record<string, number>;
  isFeatured: boolean;
  /** null when unauthenticated OR when signed in and not yet reacted. */
  userReaction: StoryReaction | null;
  createdAt: string;
}

export interface VoiceUpdate {
  id: string;
  updateType: string;
  title: string | null;
  content: string;
  sourceUrl: string | null;
  isPinned: boolean;
  user: { id: string; name: string; avatarUrl: string | null } | null;
  provider: { id: string; name: string } | null;
  createdAt: string;
}

export interface VoiceAction {
  id: string;
  actionType: string;
  title: string;
  description: string | null;
  externalUrl: string | null;
  templateContent: string | null;
  /** One per member, however often they pressed. */
  completionCount: number;
  /** Whether the signed-in reader completed it; false signed out. */
  completedByMe: boolean;
}

export function listVoicesByType(type: string, params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ voices: Voice[] }>>(
    `/voices/type/${type}`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

/**
 * On a sensitive or contested post the server withholds the identities from
 * everyone but the author: `stands` comes back EMPTY with `identitiesHidden`
 * true and a stance `composition` instead. Empty-because-withheld and
 * empty-because-nobody-stood must never render the same way.
 */
export function listVoiceStands(id: string, params?: { page?: number; limit?: number }) {
  return api.get<
    ApiResponse<{
      stands: VoiceStand[];
      identitiesHidden?: boolean;
      composition?: Record<string, number>;
      meta: VoiceMeta;
    }>
  >(
    `/voices/${id}/stands`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function listVoiceStories(id: string, params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ stories: VoiceStory[]; meta: VoiceMeta }>>(
    `/voices/${id}/stories`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function addVoiceStory(
  id: string,
  input: { content: string; isAnonymous?: boolean; imageUrl?: string; videoUrl?: string },
) {
  // content is 10–5000 server-side; anything shorter comes back a 400.
  return api.post<ApiResponse<{ story: VoiceStory }>>(`/voices/${id}/stories`, input);
}

export function deleteVoiceStory(storyId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/voices/stories/${storyId}`);
}

export function reactToVoiceStory(storyId: string, reactionType: StoryReaction) {
  return api.post<ApiResponse<{ success: boolean; reactionType: StoryReaction }>>(
    `/voices/stories/${storyId}/react`,
    { reactionType },
  );
}

export function removeVoiceStoryReaction(storyId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/voices/stories/${storyId}/react`);
}

export function listVoiceUpdates(id: string, params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<{ updates: VoiceUpdate[]; meta: VoiceMeta }>>(
    `/voices/${id}/updates`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function addVoiceUpdate(
  id: string,
  input: {
    updateType: (typeof UPDATE_TYPES)[number];
    content: string;
    title?: string;
    sourceUrl?: string;
    providerId?: string;
  },
) {
  return api.post<ApiResponse<{ update: VoiceUpdate }>>(`/voices/${id}/updates`, input);
}

export function listVoiceActions(id: string) {
  return api.get<ApiResponse<{ actions: VoiceAction[] }>>(`/voices/${id}/actions`);
}

export function addVoiceAction(
  id: string,
  input: {
    actionType: (typeof ACTION_TYPES)[number];
    title: string;
    description?: string;
    externalUrl?: string;
    templateContent?: string;
  },
) {
  return api.post<ApiResponse<{ action: VoiceAction }>>(`/voices/${id}/actions`, input);
}

export function completeVoiceAction(actionId: string) {
  return api.post<ApiResponse<{ success: boolean; completionCount: number; completedByMe: boolean }>>(
    `/voices/actions/${actionId}/complete`,
    {},
  );
}

export interface CommunityPulse {
  stateCode: string | null;
  /** ISO date for the START of the week, not "now". */
  weekOf: string;
  totalVoices: number;
  topCategories: { category: string; count: number; percentChange: number }[];
  intentBreakdown: Record<string, number>;
  /** The number that matters: people asking for help who nobody answered. */
  helpPostsAwaitingResponse: number;
  weekOverWeekChange: number;
}

export function getCommunityPulse(params?: { stateCode?: string }) {
  return api.get<ApiResponse<{ pulse: CommunityPulse }>>(
    "/voices/community-pulse",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface VoiceEngagementStats {
  voiceId: string;
  stanceBreakdown: Record<string, number>;
  reactionBreakdown: Record<string, number>;
  geographicSpread: { state: string; count: number }[];
  /** How fast people showed up — the shape of attention, not its size. */
  responseVelocity: { first24h: number; from24to48h: number; after48h: number };
  totalStands: number;
  totalStories: number;
  totalComments: number;
}

export function getVoiceEngagementStats(id: string) {
  return api.get<ApiResponse<{ stats: VoiceEngagementStats }>>(
    `/voices/${id}/engagement-stats`,
  );
}

/**
 * Pin one ledger entry — the author saying "this is the outcome".
 *
 * Author-only and single-pin: the server moves the pin rather than accruing a
 * second, so no read ever sees two. Pinning is the author's WHOLE editorial
 * power over the ledger — there is no delete endpoint for an update, on
 * purpose, so contradicting entries both stay.
 */
export function pinVoiceUpdate(voiceId: string, updateId: string) {
  return api.post<
    ApiResponse<{
      update: { id: string; updateType: string; sourceUrl: string | null; isPinned: boolean };
    }>
  >(`/voices/${voiceId}/updates/${updateId}/pin`, {});
}

/**
 * Claim the underlying thing was FIXED.
 *
 * The server refuses unless a pinned entry carries a SOURCE: "trust me, it's
 * sorted" closes nothing, and a record read a year later needs the link. That
 * refusal is the feature, so callers should surface the server's own sentence
 * rather than a generic failure.
 */
export function resolveVoice(id: string) {
  return api.patch<ApiResponse<{ voice: { id: string; status: string } }>>(
    `/voices/${id}`,
    { status: "resolved" },
  );
}

export function updateVoiceIntent(id: string, intentType: (typeof VOICE_INTENTS)[number]) {
  return api.patch<
    ApiResponse<{
      voice: { voiceId: string; intentType: string; isBoosted: boolean; updatedAt: string };
    }>
  >(`/voices/${id}/intent`, { intentType });
}
